import { C } from './tokens';

// =============== Number / value formatters ===============

export const fmtPct = (val, digits = 2) => {
  if (val == null || Number.isNaN(val)) return '—';
  return `${val >= 0 ? '+' : ''}${(val * 100).toFixed(digits)}%`;
};

export const fmtNum = (val, digits = 2) => {
  if (val == null || Number.isNaN(val)) return '—';
  return Number(val).toFixed(digits);
};

export const fmtPrice = (val, locale = 'en-US') => {
  if (val == null || Number.isNaN(val)) return '—';
  return Number(val).toLocaleString(locale, { maximumFractionDigits: 2 });
};

export const fmtValue = (val, locale = 'en-US') => {
  if (val == null || Number.isNaN(val)) return '—';
  if (val >= 1e12) return `${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
  return Number(val).toLocaleString(locale, { maximumFractionDigits: 0 });
};

// =============== Locale / sector / localized text ===============

export const getLocale = (language) => (
  (language || '').toLowerCase().startsWith('vi') ? 'vi-VN' : 'en-US'
);

export const getLocalizedText = (value, language) => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ((language || '').toLowerCase().startsWith('vi')) {
      return value.vi || '—';
    }
    return value.en || '—';
  }
  return '—';
};

export const prettySector = (s, language = 'en') => {
  if (!s) return '—';
  let raw;
  if (typeof s === 'string') {
    raw = s;
  } else if (language?.startsWith('vi')) {
    raw = s?.vi || '';
  } else {
    raw = s?.en || '';
  }
  if (!raw) return '—';
  return raw.replace(/^L\d+_/, '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
};

// =============== Style tone helpers (frontend = renderer only) ===============
// These map backend enum codes to design tokens. They do not invent thresholds
// from raw metrics — backend already owns the decision; UI only paints it.

export const distToResistanceTone = (dist) => {
  if (dist == null || !Number.isFinite(dist)) return { color: C.mutedSoft };
  if (dist < 0) return { color: C.blue };
  if (dist <= 0.03) return { color: C.green };
  if (dist <= 0.06) return { color: C.amber };
  return { color: C.red };
};

export const regimeStyle = (r) => {
  switch (r) {
    case 'RISK_ON': return { bg: C.greenSoft, fg: C.green };
    case 'RISK_OFF': return { bg: C.redSoft, fg: C.red };
    case 'RECOVERY': return { bg: C.blueSoft, fg: C.blue };
    case 'NARROW_RALLY': return { bg: C.amberSoft, fg: C.amber };
    default: return { bg: '#f1f5f9', fg: C.muted };
  }
};

export const signalStyle = (s) => {
  if (s === 'BUY') return { bg: C.greenSoft, fg: C.green, border: '#a7e0bf' };
  if (s === 'BUY_WATCH') return { bg: C.blueSoft, fg: C.blue, border: '#b9c8f5' };
  if (s === 'SKIP') return { bg: C.redSoft, fg: C.red, border: '#fecaca' };
  return { bg: '#f1f5f9', fg: C.muted, border: '#dde2ea' };
};

export const triggerStyle = (t) => {
  switch ((t || '').toUpperCase()) {
    case 'BREAKOUT': return { bg: C.greenSoft, fg: C.green };
    case 'EARLY': return { bg: C.blueSoft, fg: C.blue };
    case 'RETEST': return { bg: C.cyanSoft, fg: C.cyan };
    case 'NEAR_RES': return { bg: C.amberSoft, fg: C.amber };
    default: return { bg: '#f1f5f9', fg: C.muted };
  }
};

export const tierStyle = (t) => {
  switch ((t || '').toUpperCase()) {
    case 'LEADER': return { bg: C.greenSoft, fg: C.green, code: 'LEADER' };
    case 'EMERGING': return { bg: C.blueSoft, fg: C.blue, code: 'EMERGING' };
    case 'NEUTRAL': return { bg: '#f1f5f9', fg: C.muted, code: 'NEUTRAL' };
    case 'REST': return { bg: '#f1f5f9', fg: C.muted, code: 'REST' };
    case 'WEAK': return { bg: C.redSoft, fg: C.red, code: 'WEAK' };
    default: return null;
  }
};

export const planProfileStyle = (profile) => {
  switch ((profile || '').toUpperCase()) {
    case 'OFFENSIVE': return { bg: C.greenSoft, fg: C.green };
    case 'BALANCED': return { bg: C.blueSoft, fg: C.blue };
    case 'RECOVERY': return { bg: C.blueSoft, fg: C.blue };
    case 'SELECTIVE': return { bg: C.amberSoft, fg: C.amber };
    case 'DEFENSIVE': return { bg: C.amberSoft, fg: C.amber };
    case 'CAPITAL_PRESERVATION': return { bg: C.redSoft, fg: C.red };
    default: return { bg: '#f1f5f9', fg: C.muted };
  }
};

export const pickScoreColor = (score) => {
  if (score >= 90) return C.green;
  if (score >= 80) return C.blue;
  if (score >= 70) return C.amber;
  return C.muted;
};
